// THE TEAM CHAT IN AN AI ANSWER — the `chat-channel` view the assistant
// mounts in its thread and a Fly page places as a section (workspace
// functions/assistantAppViews.js, the `views[]` contract).
//
// chatAiViews() is the PROVIDER, read from the chat EAGER slice; the view's
// component rides the lazy chat context (`fold: 'chat'` — the shell folds it
// through ensureChatContext before a card mounts). One view, three answers,
// in the shell's list frame (workspace components/AssistantViewList.js):
//
//   channel named   that channel's (or direct message's) last messages,
//                   newest first — only the unread ones with `unread`
//   unread          the unread messages of every channel, per channel
//   neither         every channel this member has, with its unread count
//
// The unread count is the sidebar's own (chatUtils unreadCount over the
// workspace's newest messages and the member's last read), so the card and
// the sidebar badges agree. The reads are the chat page's own (channels,
// members, the workspace's newest 500 messages, and the named channel's own
// read — a quiet channel's messages are older than the workspace's newest),
// into the view's OWN state, never root.messagesByChannel. A row opens the
// channel (or the message) in Chat. Nothing here writes — reading a card does
// not mark a channel read.
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

export function chatAiViews() {
  return {
    app: {
      id: 'system/chat',
      navbar: [{ label: 'shell.app.chat', route: '/chat', icon: 'chat' }],
      apps: [{ icon: 'chat' }]
    },
    fold: 'chat',
    views: [
      {
        id: 'chat-channel',
        title: 'chat.page.chat.title',
        describe:
          'Team chat: the last messages of one channel or direct message, the unread messages, or every channel with its unread count.',
        component: 'ChatChannelAnswerView',
        icon: 'chat',
        hue: 'cerulean',
        size: 'l',
        params: {
          channel: {
            type: 'string',
            describe:
              "a channel name, or a teammate's name for a direct message; empty = every channel"
          },
          unread: { type: 'boolean', describe: 'only unread messages', default: false },
          limit: { type: 'number', min: 1, max: 50, default: 12 }
        },
        link: { route: '/chat', query: {} },
        access: { route: '/chat' }
      }
    ]
  }
}

// The member's conversations in this workspace, as the sidebar shows them:
// the channels it may see (isChannelVisibleInRail) and the direct messages
// the sidebar lists — chatUtils buildDMChannels, so two records with the same
// teammate read as ONE row (the first, as in the sidebar) and a `dm` with
// several others reads as the group it is — minus the ones this member hid;
// each with its display words and the member's last read.
export function chatAnswerChannels(channels, members) {
  const el = this
  const root = (el && el.state && el.state.root) || {}
  const norm = (e) => (typeof e === 'string' ? e.toLowerCase().trim() : '')
  const me = norm(root.currentUserEmail)
  const rows = Array.isArray(members) ? members : []
  const list = (Array.isArray(channels) ? channels : []).filter(
    (c) => c && c.id != null && !c.isArchived
  )
  const mine = new Map()
  for (const m of rows) {
    if (m && norm(m.member_email) === me && m.channel_id != null) mine.set(String(m.channel_id), m)
  }
  const activeWorkspaceId = el.call('getActiveWorkspaceId', root)
  const people = el.call('peopleByEmail', root) || {}
  // buildDMChannels reads a root; this one carries the card's own rows (as
  // copies — it names a `dm` with several others a `group_dm` in place).
  const scope = {
    currentUserEmail: root.currentUserEmail,
    people: root.people,
    channels: list.map((c) => ({ ...c })),
    channelMembers: rows,
    workspace: root.workspace,
    activeWorkspaceId: root.activeWorkspaceId,
    jwtClaims: root.jwtClaims,
    org: root.org
  }
  const dms = new Map()
  for (const external of [false, true]) {
    for (const c of el.call('buildDMChannels', scope, external) || []) dms.set(String(c.id), c)
  }
  const hidden = new Set(
    (Array.isArray(root._hiddenChannels) ? root._hiddenChannels : []).map(String)
  )
  const out = []
  for (const c of list) {
    const id = String(c.id)
    if (hidden.has(id)) continue
    const shown =
      c.kind !== 'channel'
        ? dms.get(id)
        : el.call('isChannelVisibleInRail', c, { isMember: mine.has(id), activeWorkspaceId }) && c
    if (!shown) continue
    const name = String(
      el.call('polyglot', el.call('channelDisplayName', shown, rows, people, root.currentUserEmail))
    )
    out.push({
      id,
      kind: shown.kind,
      display: shown.kind === 'channel' ? '#' + name : name,
      lastReadAt: (mine.get(id) || {}).last_read_at || null
    })
  }
  return out
}

// The read: the channels, the members (last read), the workspace's newest
// messages, and — when the ask names a conversation — that conversation's
// own messages. `{ data: { channels, members, messages, focusId, named },
// failed }`.
export async function chatChannelAnswerLoad(el, params) {
  const host = el || this
  const p = params && typeof params === 'object' ? params : {}
  const root = (host && host.state && host.state.root) || {}
  const sdk = (host && host.sdk) || host.call('getSDK')
  const empty = { channels: [], members: [], messages: [], focusId: null, named: false }
  if (!sdk || typeof sdk.execute !== 'function') return { data: empty, failed: true }
  const rowsOf = (res) =>
    Array.isArray(res) ? res : res && Array.isArray(res.data) ? res.data : []
  let failed = false
  const [chRes, memRes, msgRes] = await Promise.all([
    Promise.resolve()
      .then(() => sdk.execute('workspaceProject.chat', 'list'))
      .catch(() => {
        failed = true
        return []
      }),
    Promise.resolve()
      .then(() => sdk.execute('workspaceProject.chat.members', 'list'))
      .catch(() => []),
    Promise.resolve()
      .then(() =>
        sdk.execute('workspaceProject.chat.messages', 'list', {
          options: { limit: 500, order: 'created_at' }
        })
      )
      .catch(() => [])
  ])
  const channels = host.call(
    'filterChannelsToActiveWorkspace',
    rowsOf(chRes).map((r) => host.call('rowToChannel', r)),
    host.call('getActiveWorkspaceId', root)
  )
  const members = host.call('scopeMembersToActiveWorkspace', rowsOf(memRes), root) || []
  const byId = new Map()
  for (const r of rowsOf(msgRes)) {
    if (!r || r.id == null || !host.call('rowInActiveWorkspace', root, r)) continue
    byId.set(String(r.id), host.call('rowToMessage', r))
  }
  const ask = String(p.channel || '')
    .replace(/^[#@]/, '')
    .trim()
    .toLowerCase()
  let focusId = null
  if (ask) {
    const list = host.call('chatAnswerChannels', channels, members)
    const words = ask.split(/\s+/).filter(Boolean)
    const bare = (c) => c.display.replace(/^#/, '').toLowerCase()
    const hit =
      list.find((c) => bare(c) === ask) || list.find((c) => words.every((w) => bare(c).includes(w)))
    focusId = hit ? hit.id : null
    if (focusId) {
      const own = await Promise.resolve()
        .then(() => sdk.execute('workspaceProject.chat.messages', 'list', { channelId: focusId }))
        .catch(() => [])
      for (const r of rowsOf(own)) {
        if (r && r.id != null && !byId.has(String(r.id)))
          byId.set(String(r.id), host.call('rowToMessage', r))
      }
    }
  }
  return {
    data: { channels, members, messages: [...byId.values()], focusId, named: !!ask },
    failed: failed && !channels.length
  }
}

// The model — see the header: a named conversation's messages, the unread
// messages per conversation, or every conversation with its unread count.
export function chatChannelAnswerModel(data, params, failed) {
  const el = this
  const root = (el && el.state && el.state.root) || {}
  const d = data && typeof data === 'object' ? data : {}
  const p = params && typeof params === 'object' ? params : {}
  const say = (key) => String(el.call('polyglot', key))
  if (failed) return { groups: [], note: 'chat.answer.failed' }
  const limit = Math.max(1, Math.min(50, Math.floor(Number(p.limit)) || 12))
  const onlyUnread = p.unread === true || p.unread === 'true'
  const me = String(root.currentUserEmail || '').toLowerCase()
  const list = el.call('chatAnswerChannels', d.channels, d.members) || []
  const byChannel = {}
  for (const m of Array.isArray(d.messages) ? d.messages : []) {
    if (!m || m.channelId == null) continue
    const k = String(m.channelId)
    ;(byChannel[k] = byChannel[k] || []).push(m)
  }
  for (const k of Object.keys(byChannel)) byChannel[k] = el.call('sortMessagesByTime', byChannel[k])
  const hasWords = (m) =>
    !m.deleted &&
    ((typeof m.body === 'string' && m.body.trim()) ||
      (Array.isArray(m.attachments) && m.attachments.length))
  const isUnread = (m, c) => {
    if (String(m.authorEmail || '').toLowerCase() === me) return false
    const cut = c.lastReadAt ? Date.parse(c.lastReadAt) || 0 : 0
    return (Date.parse(m.createdAt || '') || 0) > cut
  }
  const unreadOf = (c) =>
    el.call('unreadCount', byChannel[c.id] || [], c.lastReadAt, root.currentUserEmail)
  const people = el.call('peopleByEmail', root) || {}
  const words = (body) =>
    String(body || '')
      .replace(/```[\s\S]*?```/g, ' ')
      .replace(/`([^`]*)`/g, '$1')
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/<[^>]+>/g, ' ')
      .replace(/^\s{0,3}(#{1,6}|>+|[-*+]\s|\d+\.\s)\s*/gm, '')
      .replace(/[*_~]{1,3}([^*_~]+)[*_~]{1,3}/g, '$1')
      .replace(/\s+/g, ' ')
      .trim()
  const when = (iso) => {
    const ms = Date.parse(iso || '')
    if (!Number.isFinite(ms)) return ''
    const sameDay = new Date(ms).toDateString() === new Date().toDateString()
    return String(
      el.call(
        'localizedDate',
        new Date(ms),
        sameDay ? { hour: '2-digit', minute: '2-digit' } : { month: 'short', day: 'numeric' }
      ) || ''
    )
  }
  const messageRow = (m) => {
    const person = people[m.authorEmail] || null
    const who = (person && person.name) || String(m.authorEmail || '').split('@')[0] || ''
    const text = words(m.body)
    return el.call('assistantViewRow', {
      key: 'msg-' + String(m.id).replace(/[^a-zA-Z0-9_-]/g, ''),
      title:
        text.length > 220
          ? text.slice(0, 219).replace(/\s+\S*$/, '') + '…'
          : text || say('chat.answer.attachment'),
      meta: [who, when(m.createdAt)].filter(Boolean).join(' · '),
      src: (person && person.avatar) || null,
      initials: person && person.avatar ? null : el.call('initials', who) || null,
      open: {
        call: 'openAppPage',
        arg:
          '/chat?channel=' +
          encodeURIComponent(m.channelId) +
          '&message=' +
          encodeURIComponent(m.id)
      }
    })
  }
  const unreadLabel = (c, n) =>
    c.display + (n ? ' · ' + say('chat.answer.unreadCount').replace('{count}', String(n)) : '')
  if (d.focusId) {
    const c = list.find((x) => x.id === String(d.focusId))
    if (!c) return { groups: [], note: 'chat.answer.noChannel' }
    const all = (byChannel[c.id] || []).filter(hasWords)
    const timeline = onlyUnread
      ? all.filter((m) => isUnread(m, c))
      : all.filter((m) => !m.threadRoot)
    const newest = timeline.slice().reverse()
    const rows = newest.slice(0, limit).map(messageRow)
    return {
      groups: rows.length
        ? [
            el.call('assistantViewGroup', {
              key: 'focus',
              label: unreadLabel(c, unreadOf(c)),
              rows
            })
          ]
        : [],
      note: rows.length ? '' : onlyUnread ? 'chat.answer.noUnread' : 'chat.answer.noMessages',
      more: { shown: rows.length, total: newest.length }
    }
  }
  if (d.named) return { groups: [], note: 'chat.answer.noChannel' }
  const lastAt = (c) => {
    const msgs = byChannel[c.id] || []
    return msgs.length ? Date.parse(msgs[msgs.length - 1].createdAt || '') || 0 : 0
  }
  if (onlyUnread) {
    const withUnread = list
      .map((c) => ({
        c,
        msgs: (byChannel[c.id] || []).filter((m) => hasWords(m) && isUnread(m, c))
      }))
      .filter((x) => x.msgs.length)
      .sort((a, b) => lastAt(b.c) - lastAt(a.c))
    const groups = []
    let budget = limit
    let shown = 0
    let total = 0
    for (const { c, msgs } of withUnread) {
      total += msgs.length
      const rows = msgs.slice().reverse().slice(0, Math.max(0, budget)).map(messageRow)
      budget -= rows.length
      shown += rows.length
      if (rows.length) {
        groups.push(
          el.call('assistantViewGroup', {
            key: 'ch-' + c.id,
            label: unreadLabel(c, unreadOf(c)),
            rows
          })
        )
      }
    }
    return { groups, note: groups.length ? '' : 'chat.answer.noUnread', more: { shown, total } }
  }
  const ordered = list.slice().sort((a, b) => unreadOf(b) - unreadOf(a) || lastAt(b) - lastAt(a))
  const rows = ordered.slice(0, limit).map((c) => {
    const n = unreadOf(c)
    const last = (byChannel[c.id] || []).slice(-1)[0]
    return el.call('assistantViewRow', {
      key: 'ch-' + String(c.id).replace(/[^a-zA-Z0-9_-]/g, ''),
      title: c.display,
      meta: n
        ? say('chat.answer.unreadCount').replace('{count}', String(n))
        : last
          ? when(last.createdAt)
          : '',
      icon: c.kind === 'channel' ? 'chat' : 'people',
      open: { call: 'openAppPage', arg: '/chat?channel=' + encodeURIComponent(c.id) }
    })
  })
  return {
    groups: rows.length
      ? [
          el.call('assistantViewGroup', {
            key: 'channels',
            label: say('chat.sidebar.conversations'),
            rows
          })
        ]
      : [],
    note: rows.length ? '' : 'chat.answer.noMessages',
    more: { shown: rows.length, total: ordered.length }
  }
}
